import React, { useContext } from "react";
import "./../css/fontello/css/fontello.css";
import "./../css/style.css";
import {
	CircularProgress,
	Container,
	Paper,
	Box,
	Grid,
	Alert,
	Badge,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
	Text,
	Blocks,
	Sidebar,
	List,
	Tags,
	Banner,
	TitleBar,
	TitleBlog,
	GlobalSearch,
	DownloadPage,
	Date,
	ProfilePage,
	getSingleLayoutSizes,
	MoreButton,
	Divider,
	Player,
	FieldsDisplay,
	Image,
	AddToCart,
	Share,
} from "../";
import { findRestrictionMessage } from "gatsby-frontend";
import { SiteContext } from "gatsby-frontend/src/Site";
import { SingleContext } from "gatsby-frontend/src/Page";
import ContactPage from "./site/ContactPage";

function SingleBody({ children }) {
	const theme = useTheme();
	const singleContext = useContext(SingleContext);
	const siteContext = useContext(SiteContext);

	const {
		type,
		title,
		title_append,
		attributes,
		relationships,
		hasMainSection,
		hasClosingSection,
		hasSidebar,
		hasBody,
		hasIntro,
		hasBanner,
		hasVideo,
		bannerLayout,
		addToCart,
		hasBodyMedia,
		hasSidebarMedia,
		hasTags,
		hasFieldsDisplay,
		hasFixedFeaturedImage,
		tagsPosition,
		isHome,
		media,
		tags,
		intro,
		banner,
		hasDates,
		hasExternalLink,
		externalLink,
		dates,
		disableTitleBar,
		sidebarSize,
		vCard,
		image,
		video,
		fieldsDisplay,
		shareButtons,
		dateFormat = "D",
		gutterSize = 4,
		sidebarStyle,
		isReversed,
		logoPrint,
		newBadge,
		isSideShare,
	} = singleContext;

	const { themeConfig, isRestricted } = siteContext;

	const hasShare = shareButtons && shareButtons.length > 0;

	console.warn("hasShare", hasShare, shareButtons);

	let titleBarImage =
		bannerLayout === "background" && banner ? banner.image : null;

	const [grid_left_size, grid_right_size] = getSingleLayoutSizes(
		hasSidebar,
		hasTags && tagsPosition === "side",
		sidebarSize
	);

	function TagsWrap({ children }) {
		return sidebarStyle === "box" ? (
			<Paper sx={{ padding: theme.spacing(2) }} className="sidebar-block">
				{children}
			</Paper>
		) : (
			<>{children}</>
		);
	}

	// Logic to order the tasks in case it's needed
	const tagOrder = themeConfig?.customOptions?.tagOrder;
	let modifiedTags = [];

	if (tagOrder && typeof tagOrder === "object" && tags) {
		modifiedTags = tags
			.map((group) => {
				const order = tagOrder[group.label] || 6;
				return { ...group, order };
			})
			.sort((a, b) => a.order - b.order);
	} else {
		modifiedTags = tags || [];
	}

	const sortedTags = modifiedTags.sort((a, b) => a.order - b.order);

	if (children) {
		return <main>{children}</main>;
	}

	if (isRestricted) {
		return (
			<main>
				<Container
					maxWidth={theme?.customOptions?.containerMaxWidth || "xl"}
					sx={{ padding: theme.spacing(3, 0) }}
					className="main-container"
				>
					<Box className="main-wrapper">
						<Alert
							severity="warning"
							sx={{ "& p": { margin: 0 }, justifyContent: "center" }}
						>
							<div>{findRestrictionMessage(isRestricted)[0]}</div>
						</Alert>
					</Box>
				</Container>
			</main>
		);
	}

	if (!attributes) {
		return (
			<Box sx={{ textAlign: "center" }}>
				<CircularProgress sx={{ margin: "2rem auto" }} />
			</Box>
		);
	}

	if (type === "search-page") {
		return (
			<main>
				<GlobalSearch {...singleContext} />
			</main>
		);
	}

	if (type === "download-page") {
		return (
			<main>
				<DownloadPage {...singleContext} />
			</main>
		);
	}

	if (type === "profile-page") {
		return (
			<main>
				<ProfilePage {...singleContext} />
			</main>
		);
	}

	if (type === "contact-page") {
		return (
			<main>
				<ContactPage {...singleContext} />
			</main>
		);
	}

	return (
		<main>
			<Box
				className="print-only"
				sx={{ display: "none", "& img": { width: 200, height: "auto" } }}
			>
				<img src={logoPrint} alt="logo" />
			</Box>

			{!isHome && !disableTitleBar && (
				<TitleBar
					title={title}
					title_append={title_append}
					subtitle={attributes.field_subtitle}
					image={titleBarImage}
					vCard={vCard}
					layout={bannerLayout}
					shareButtons={shareButtons}
				/>
			)}

			{hasBanner && <Banner {...banner} />}

			{(hasMainSection ||
				hasBody ||
				hasFieldsDisplay ||
				hasVideo ||
				hasIntro ||
				hasBodyMedia ||
				hasTags ||
				hasSidebar ||
				hasDates ||
				hasExternalLink) && (
				<Container
					maxWidth={theme?.customOptions?.containerMaxWidth || "xl"}
					sx={{ padding: theme.spacing(3, 0) }}
					className="main-container"
				>
					<Box className="main-wrapper">
						{newBadge ? (
							<Badge
								badgeContent={"New"}
								style={{ marginLeft: "15px" }}
								color="secondary"
							></Badge>
						) : null}
						{bannerLayout === "blog" && (
							<TitleBlog
								title={title}
								title_append={title_append}
								subtitle={attributes.field_subtitle}
								hasDates={hasDates}
								dates={dates}
								dateFormat={dateFormat}
								image={image}
								vCard={vCard}
								isReversed={isReversed}
								shareButtons={shareButtons}
							/>
						)}

						{hasIntro && (
							<Box className="intro-box">
								<Text html={intro} />
								<Divider size="lg" />
							</Box>
						)}

						<Grid
							container
							className={`main-grid-container ${
								grid_left_size === 12 ? "no-sidebar" : "has-sidebar"
							}`}
							spacing={grid_left_size === 12 ? 0 : gutterSize}
						>
							<Grid
								className="main-grid-left"
								item
								sm={grid_left_size}
								sx={{ maxWidth: "100%" }}
							>
								<Box
									className={`body-box ${
										hasShare && isSideShare ? "side-share" : ""
									}`}
									component={themeConfig?.customOptions?.flush ? "div" : Paper}
									elevation={3}
									sx={{
										...(hasShare &&
											isSideShare && {
												position: "relative",
												"& .share-buttons": {
													position: "absolute",
													top: 0,
													left: theme.spacing(-1),
													zIndex: 1,
													padding: 0,
													display: "flex",
													flexDirection: "column",
												},
											}),
										padding: themeConfig?.customOptions?.flush
											? 0
											: theme.spacing(3, 4),
									}}
								>
									{isSideShare && hasShare && (
										<Share
											buttons={shareButtons}
											color={theme.palette.body.contrastText}
											shareLocation={theme?.customOptions?.shareLocation}
										/>
									)}

									<Box
										className="body-box-inner"
										sx={{
											padding:
												hasShare && isSideShare ? "0 0 0 3em" : "0 0 0 0",
											width: "100%",
										}}
									>
										{hasDates && bannerLayout !== "blog" && (
											<Date dates={dates} single format={dateFormat} />
										)}

										{hasFixedFeaturedImage && (
											<Box
												className="fixed-featured-image"
												sx={{ marginBottom: theme.spacing(3) }}
											>
												<Image {...image} />
											</Box>
										)}

										{hasVideo && (
											<Box
												sx={{
													position: "relative",
													marginBottom: theme.spacing(3),
												}}
											>
												<Player {...video} autoplay={false} />
											</Box>
										)}

										{hasFieldsDisplay && fieldsDisplay.prepend && (
											<Box
												className="fixed-fields-display prepend"
												sx={{ marginBottom: theme.spacing(2) }}
											>
												<FieldsDisplay {...fieldsDisplay.prepend} />
											</Box>
										)}

										{hasBody && (
											<Text
												html={
													attributes.body.transformed
														? attributes.body.transformed
														: attributes.body.value
												}
											/>
										)}

										{hasMainSection && (
											<Blocks
												data={relationships.field_section_main.data}
												className="main-section"
											/>
										)}

										{hasBodyMedia && (
											<List
												{...media}
												useElastic={false}
												cols={2}
												layout="topbar"
												sx={{ marginTop: theme.spacing(3) }}
											/>
										)}

										{hasExternalLink && (
											<MoreButton
												{...externalLink}
												sx={{ marginTop: theme.spacing(4) }}
											/>
										)}

										{hasFieldsDisplay && fieldsDisplay.append && (
											<Box
												className="fixed-fields-display append"
												sx={{ marginTop: theme.spacing(2) }}
											>
												<FieldsDisplay {...fieldsDisplay.append} />
											</Box>
										)}

										{hasTags && tagsPosition === "bottom" && (
											<Box className="tags-bottom">
												<Divider size="xl" />
												{tags.map((group) => (
													<Tags
														inline
														key={group.label}
														{...group}
														chips
														headingType="body2"
														sx={{ marginTop: theme.spacing(1) }}
													/>
												))}
											</Box>
										)}
									</Box>
								</Box>
							</Grid>

							{(hasSidebar ||
								(hasTags && tagsPosition === "side") ||
								hasSidebarMedia ||
								addToCart) && (
								<Grid item sm={grid_right_size} className="main-grid-right">
									{hasTags && tagsPosition === "side" && (
										<TagsWrap>
											{sortedTags.map((group) => (
												<Tags key={group.label} {...group} />
											))}
										</TagsWrap>
									)}
									{hasSidebar && (
										<Sidebar
											blocks={relationships.field_section_sidebar.data}
										/>
									)}
									{(hasSidebarMedia || addToCart) && (
										<Box className="sidebar-actions">
											{addToCart && <AddToCart />}
											{hasSidebarMedia && (
												<List {...media} useElastic={false} cols={1} />
											)}
										</Box>
									)}
								</Grid>
							)}
						</Grid>
					</Box>
				</Container>
			)}
			{hasClosingSection && (
				<Blocks
					data={relationships.field_section_closing.data}
					fullWidth
					className="closing-section"
				/>
			)}
		</main>
	);
}

export default SingleBody;
